import {
  emptyCollectionStream,
  toCollectionStream,
} from 'streams/utils/streamUtils';
import { APIPresentationAssetAttributes, ResourceType } from '@ardoq/api-types';
import {
  reducedStream,
  reducer,
  streamReducer,
  toPersistentStream,
} from '@ardoq/rxbeach';
import { fetchAllReducer, websocketReducer } from 'streams/crud/reducers';
import { filterResourceType, websocket$ } from 'sync/websocket$';
import { combineLatest, map } from 'rxjs';
import { presentationOperations } from './presentationOperations';
import { PermissionContext } from '@ardoq/access-control';
import {
  removeEntityById,
  returnPayload,
  updateEntity,
} from '@ardoq/common-helpers';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { ardoqEventOperations } from 'sync/ardoqEventOperations';
import { ArdoqEvent } from 'sync/types';
import {
  notifyPresentationCreatedByUser,
  updatePresentation,
  updatePresentationSucceeded,
} from './actions';

export const presentationsNamespace = 'presentations';
export const isPresentationEvent = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<APIPresentationAssetAttributes> => {
  return ardoqEventOperations.isOfResourceType(
    event,
    ResourceType.PRESENTATION
  );
};

const contains = (
  state: APIPresentationAssetAttributes[],
  { _id }: APIPresentationAssetAttributes
): boolean => state.some(existing => existing._id === _id);

const handleAddedPresentation = (
  state: APIPresentationAssetAttributes[],
  presentation: APIPresentationAssetAttributes
): APIPresentationAssetAttributes[] =>
  contains(state, presentation) ? state : [presentation, ...state];

const handlePresentationCreatedByUser = (
  state: APIPresentationAssetAttributes[],
  presentation: APIPresentationAssetAttributes
): APIPresentationAssetAttributes[] =>
  contains(state, presentation)
    ? updateEntity(state, presentation)
    : [presentation, ...state];

const presentationsList$ = reducedStream<APIPresentationAssetAttributes[]>(
  'presentationsList$',
  [],
  [
    fetchAllReducer(returnPayload<APIPresentationAssetAttributes[]>),
    streamReducer(
      websocket$.pipe(
        filterResourceType<APIPresentationAssetAttributes>(
          ResourceType.PRESENTATION
        )
      ),
      websocketReducer({
        create: handleAddedPresentation,
        update: updateEntity,
        delete: removeEntityById,
      })
    ),
    reducer(notifyPresentationCreatedByUser, handlePresentationCreatedByUser),
  ],
  { namespace: presentationsNamespace }
);

/** Filter out presentations that are not editable and have no slides. */
const getVisiblePresentations = (
  presentations: APIPresentationAssetAttributes[],
  currentUserPermissionContext: PermissionContext
): APIPresentationAssetAttributes[] =>
  presentations.filter(presentation =>
    presentationOperations.isEditableOrHasSlides(
      presentation,
      currentUserPermissionContext
    )
  );

const visiblePresentations$ = combineLatest({
  currentUserPermissionContext: currentUserPermissionContext$,
  presentations: presentationsList$,
}).pipe(
  map(({ presentations, currentUserPermissionContext }) => {
    return getVisiblePresentations(presentations, currentUserPermissionContext);
  })
);

const presentations$ = toPersistentStream(
  'presentations$',
  visiblePresentations$.pipe(map(toCollectionStream)),
  emptyCollectionStream()
);

const optimisticUpdatePresentation = (
  state: APIPresentationAssetAttributes[],
  updated: APIPresentationAssetAttributes
) => {
  return state.map(existingPresentation => {
    return existingPresentation._id === updated._id
      ? updated
      : existingPresentation;
  });
};

// This is a hack to get around flickering when doing
// drag and drop with slides. Please don't follow this implementation,
// as it doesn't handle errors correctly
export const optimisticPresentations$ = reducedStream(
  'optimisticPresentations$',
  [],
  [
    streamReducer(
      visiblePresentations$,
      returnPayload<APIPresentationAssetAttributes[]>
    ),
    reducer(updatePresentation, optimisticUpdatePresentation),
    reducer(updatePresentationSucceeded, optimisticUpdatePresentation),
  ]
).pipe(map(toCollectionStream));

export { presentations$ };
